import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildQueryFilters,
  buildQueryArraySorters,
  MAXIMUM_PAGINATION,
  SpendAreaStage,
} from '@sib/api';
import {
  useActionItemList,
  SpendAreaStatus,
  useSpendAreas as useSpendAreasData,
  SpendAreaDto,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { ProjectStatus, SpendAreaListItem } from '@spendbrain/types/project';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import {
  intermediateStages,
  stageToSpendCategoryMap,
  statusOrder,
} from './UserSpendAreas.constants';

export const getSortSpendAreas = (activeStage: ProjectStatus) => {
  const isAllActive = activeStage === ProjectStatus.All;

  return (a: SpendAreaListItem, b: SpendAreaListItem) => {
    const aIsActive = a.isActive;
    const bIsActive = b.isActive;

    if (!isAllActive) {
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;

      if (a.stage.length > b.stage.length) return 1;
      if (a.stage.length < b.stage.length) return -1;

      return 0;
    }

    const statusComparison =
      statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    if (statusComparison !== 0) return statusComparison;

    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;

    return 0;
  };
};

export const formatStatusProjectDate = (dateString: string, locale = enUS) => {
  const parsedDate = new Date(dateString);

  return format(parsedDate, "do 'of' MMM", { locale });
};

export const getAdjustedSpendAreaStatus = ({
  service,
  activeStage,
}: {
  service: SpendAreaDto;
  activeStage: ProjectStatus;
}) => {
  const belongsToMultipleStages = service.stage.length > 1;
  if (
    intermediateStages.includes(activeStage as ProjectStatus) &&
    belongsToMultipleStages &&
    service.status === SpendAreaStatus.COMPLETED
  ) {
    return SpendAreaStatus.IN_PROGRESS;
  }

  if (
    (activeStage === ProjectStatus.All ||
      activeStage === ProjectStatus.MonitoringSavings) &&
    service.status === SpendAreaStatus.COMPLETED
  ) {
    return SpendAreaStatus.COMPLETED;
  }

  return service.status;
};

export interface ProjectTabItem {
  id: string;
  name: string;
  itemsCount?: number;
  isActive?: boolean;
}

interface useGroupedSpendAreasProps {
  areas: SpendAreaListItem[];
}

export const useGroupedSpendAreas = ({
  areas,
}: useGroupedSpendAreasProps): Record<ProjectStatus, SpendAreaListItem[]> => {
  const grouped = useMemo(() => {
    const stages = Object.values(ProjectStatus);

    return stages.reduce<Record<string, SpendAreaListItem[]>>((acc, stage) => {
      if (stage === ProjectStatus.All)
        return {
          ...acc,
          [ProjectStatus.All]: areas,
        };

      const projectStage = stageToSpendCategoryMap[stage];

      return {
        [stage]: areas.filter(area => area.stage.includes(projectStage)),
        ...acc,
      };
    }, {});
  }, [areas]);

  return grouped;
};

export const useSpendAreas = () => {
  const [selectedProjectStatus, setSelectedProjectStatus] =
    useState<ProjectStatus>(ProjectStatus.All);

  const organizationId = useUserSetupStore(state => state.organizationId);

  const {
    data: spendAreasData,
    isLoading: spendAreasLoading,
    isFetching: spendAreasFetching,
    isRefetching: spendAreasRefetching,
    error: spendAreasQueryError,
  } = useSpendAreasData(organizationId);

  const isSpendAreasLoading =
    spendAreasLoading || spendAreasFetching || spendAreasRefetching;
  const spendAreasError = !!spendAreasQueryError || !spendAreasData;

  const {
    data: actionItemsData,
    isFetching: actionItemsFetching,
    isRefetching: actionItemsRefetching,
    isLoading: actionItemsLoading,
    error: actionItemsError,
  } = useActionItemList(organizationId, {
    page: 0,
    size: MAXIMUM_PAGINATION,
    filter: buildQueryFilters([
      {
        field: 'completedAt',
        operator: 'null',
        value: true,
      },
    ]),
    sort: buildQueryArraySorters([
      {
        field: 'createdAt',
        order: 'desc',
      },
    ]),
  });

  const areActionItemsLoading =
    actionItemsLoading || actionItemsFetching || actionItemsRefetching;

  const isActionItemsError = !!actionItemsError;

  const assignedSpendAreas = useMemo(
    () =>
      spendAreasData?.items.filter(
        i => i.status !== SpendAreaStatus.NOT_ASSIGNED
      ) || [],
    [spendAreasData]
  );

  const areThereAnyAreas = assignedSpendAreas.length > 0;

  const actionItems = useMemo(
    () => actionItemsData?.items || [],
    [actionItemsData]
  );

  const areThereAnyActionItems = actionItems.length > 0;

  const filteredServices: SpendAreaListItem[] = useMemo(() => {
    if (!spendAreasData?.items) return [];

    const sortServices = getSortSpendAreas(selectedProjectStatus);

    const filtered = spendAreasData.items
      .filter(service => service.status !== SpendAreaStatus.NOT_ASSIGNED)
      .map(item => {
        const adjustedStatus = getAdjustedSpendAreaStatus({
          activeStage: selectedProjectStatus,
          service: item,
        });
        const isActive =
          selectedProjectStatus === ProjectStatus.All ||
          item.stage.some(stage => `${stage}` === selectedProjectStatus);

        return {
          ...item,
          adjustedStatus,
          isActive,
        };
      });

    filtered.sort(sortServices);

    return filtered;
  }, [spendAreasData, selectedProjectStatus]);

  const groupedSpendAreas = useGroupedSpendAreas({ areas: filteredServices });

  return {
    areThereAnyAreas,
    selectedProjectStatus,
    setSelectedProjectStatus,
    isSpendAreasLoading,
    spendAreasError,
    isActionItemsError,
    actionItems,
    areThereAnyActionItems,
    actionItemsLoading: areActionItemsLoading,
    filteredServices,
    groupedSpendAreas,
  };
};

interface UseProjectStatusTabsProps {
  groupedAreas: Record<ProjectStatus, SpendAreaListItem[]>;
  activeStatus: ProjectStatus;
}

export const useProjectStatusTabs = ({
  groupedAreas,
  activeStatus,
}: UseProjectStatusTabsProps) => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      [
        ProjectStatus.All,
        ProjectStatus.Mobilization,
        ProjectStatus.SavingsEstimated,
        ProjectStatus.Negotiations,
        ProjectStatus.SavingsRealized,
        ProjectStatus.MonitoringSavings,
      ].map(stage => {
        const areas = groupedAreas[stage];
        const isActive = activeStatus === stage;

        return {
          id: stage,
          name: t(`enums.ProjectStatus.${stage}`),
          isActive,
          itemsCount: areas.length,
        };
      }),
    [t, groupedAreas, activeStatus]
  );
};

export const formatName = (fullName: string) => {
  const [firstName, lastName] = fullName.split(' ');
  if (!firstName || !lastName) return fullName;

  return `${firstName.charAt(0)}. ${lastName}`;
};

export const useClampOverflow = (enabled: boolean = true) => {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const [clamped, setClamped] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const el = ref.current;
    if (!el) return;

    const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => {
      ro.disconnect();
    };
  }, [enabled]);

  return { ref, clamped };
};

export const SpendAreaStageDisplayName: Record<SpendAreaStage, string> = {
  [SpendAreaStage.MOBILIZATION]: 'Mobilization',
  [SpendAreaStage.SAVINGS_ESTIMATED]: 'Analyzing Spend',
  [SpendAreaStage.NEGOTIATIONS]: 'Negotiations',
  [SpendAreaStage.SAVINGS_REALIZED]: 'Savings Realized',
  [SpendAreaStage.MONITORING_SAVINGS]: 'Monitoring Savings',
};
